import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { useLanguage } from '../i18n/LanguageContext';
import { Leaf, Recycle, TreePine, Droplet, BookOpen, Heart, CheckCircle, AlertCircle } from 'lucide-react';

const Section = ({ icon: Icon, title, color, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
    <div className={`px-6 py-4 ${color}`}>
      <div className="flex items-center gap-3">
        <Icon className="w-6 h-6 text-white" />
        <h3 className="text-lg font-bold text-white">{title}</h3>
      </div>
    </div>
    <div className="p-6 text-gray-700 leading-relaxed space-y-4">{children}</div>
  </div>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2">
    <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-1" />
    <span>{children}</span>
  </li>
);

const EnvironmentalPolicyPage = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />
      <WhatsAppButton />

      {/* Header */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-[#1D4E41] to-[#2d6a4f]">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full mb-6">
            <Leaf className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('environmental.title')}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            {t('environmental.subtitle')}
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-12">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-6 mb-10">
            <p className="text-emerald-900 leading-relaxed mb-4">{t('environmental.intro1')}</p>
            <p className="text-emerald-900 leading-relaxed">{t('environmental.intro2')}</p>
          </div>

          <div className="space-y-6">
            {/* Commitment */}
            <Section icon={Heart} title={t('environmental.commitmentTitle')} color="bg-emerald-600">
              <p>{t('environmental.commitmentIntro')}</p>
              <ul className="space-y-2">
                <Bullet>{t('environmental.commitment1')}</Bullet>
                <Bullet>{t('environmental.commitment2')}</Bullet>
                <Bullet>{t('environmental.commitment3')}</Bullet>
                <Bullet>{t('environmental.commitment4')}</Bullet>
                <Bullet>{t('environmental.commitment5')}</Bullet>
              </ul>
            </Section>

            {/* Waste Management */}
            <Section icon={Recycle} title={t('environmental.wasteTitle')} color="bg-[#1D4E41]">
              <p>{t('environmental.wasteIntro')}</p>
              <div className="bg-gray-50 rounded-xl p-5">
                <h4 className="font-semibold text-gray-800 mb-3">{t('environmental.wasteUnits')}</h4>
                <ul className="space-y-2">
                  <Bullet>{t('environmental.waste1')}</Bullet>
                  <Bullet>{t('environmental.waste2')}</Bullet>
                  <Bullet>{t('environmental.waste3')}</Bullet>
                  <Bullet>{t('environmental.waste4')}</Bullet>
                  <Bullet>{t('environmental.waste5')}</Bullet>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">{t('environmental.wasteSeparation')}</h4>
                <p className="mb-2">{t('environmental.separationIntro')}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="px-3 py-2 bg-blue-50 text-blue-800 rounded-lg text-sm text-center font-medium">{t('environmental.plastic')}</div>
                  <div className="px-3 py-2 bg-gray-200 text-gray-800 rounded-lg text-sm text-center font-medium">{t('environmental.aluminum')}</div>
                  <div className="px-3 py-2 bg-amber-50 text-amber-800 rounded-lg text-sm text-center font-medium">{t('environmental.paper')}</div>
                  <div className="px-3 py-2 bg-emerald-50 text-emerald-800 rounded-lg text-sm text-center font-medium">{t('environmental.organic')}</div>
                </div>
              </div>
            </Section>

            {/* Responsible Behavior */}
            <Section icon={TreePine} title={t('environmental.behaviorTitle')} color="bg-green-700">
              <p className="font-medium text-emerald-700">{t('environmental.behaviorIntro')}</p>

              <div className="bg-emerald-50 rounded-xl p-5">
                <h4 className="font-semibold text-emerald-800 mb-3">{t('environmental.wildlifeTitle')}</h4>
                <ul className="space-y-2">
                  <Bullet>{t('environmental.wildlife1')}</Bullet>
                  <Bullet>{t('environmental.wildlife2')}</Bullet>
                  <Bullet>{t('environmental.wildlife3')}</Bullet>
                </ul>
              </div>

              <div className="bg-blue-50 rounded-xl p-5">
                <h4 className="font-semibold text-blue-800 mb-3">{t('environmental.ecoTitle')}</h4>
                <ul className="space-y-2">
                  <Bullet>{t('environmental.eco1')}</Bullet>
                  <Bullet>{t('environmental.eco2')}</Bullet>
                  <Bullet>{t('environmental.eco3')}</Bullet>
                </ul>
              </div>

              <div className="bg-amber-50 rounded-xl p-5">
                <h4 className="font-semibold text-amber-800 mb-3">{t('environmental.wasteRespTitle')}</h4>
                <ul className="space-y-2">
                  <Bullet>{t('environmental.wasteResp1')}</Bullet>
                  <Bullet>{t('environmental.wasteResp2')}</Bullet>
                </ul>
              </div>

              <div className="bg-purple-50 rounded-xl p-5">
                <h4 className="font-semibold text-purple-800 mb-3">{t('environmental.pollutionTitle')}</h4>
                <ul className="space-y-2">
                  <Bullet>{t('environmental.pollution1')}</Bullet>
                  <Bullet>{t('environmental.pollution2')}</Bullet>
                  <Bullet>{t('environmental.pollution3')}</Bullet>
                </ul>
              </div>
            </Section>

            {/* Water and Energy */}
            <Section icon={Droplet} title={t('environmental.waterEnergyTitle')} color="bg-sky-600">
              <p>{t('environmental.waterEnergyIntro')}</p>
              <ul className="space-y-2">
                <Bullet>{t('environmental.waterEnergy1')}</Bullet>
                <Bullet>{t('environmental.waterEnergy2')}</Bullet>
                <Bullet>{t('environmental.waterEnergy3')}</Bullet>
              </ul>
            </Section>

            {/* Compliance */}
            <Section icon={AlertCircle} title={t('environmental.complianceTitle')} color="bg-gray-700">
              <p>{t('environmental.complianceIntro')}</p>
              <ul className="space-y-2">
                <Bullet>{t('environmental.compliance1')}</Bullet>
                <Bullet>{t('environmental.compliance2')}</Bullet>
                <Bullet>{t('environmental.compliance3')}</Bullet>
                <Bullet>{t('environmental.compliance4')}</Bullet>
              </ul>
            </Section>

            {/* Education */}
            <Section icon={BookOpen} title={t('environmental.educationTitle')} color="bg-orange-600">
              <p>{t('environmental.educationIntro')}</p>
              <ul className="space-y-2">
                <Bullet>{t('environmental.education1')}</Bullet>
                <Bullet>{t('environmental.education2')}</Bullet>
                <Bullet>{t('environmental.education3')}</Bullet>
              </ul>
            </Section>

            {/* Invitation */}
            <div className="bg-gradient-to-br from-emerald-500 to-[#1D4E41] rounded-2xl p-8 text-center text-white shadow-lg">
              <h3 className="text-2xl font-bold mb-4">{t('environmental.invitationTitle')}</h3>
              <p className="text-lg mb-6 italic">"{t('environmental.invitationQuote')}"</p>
              <p className="text-white/90">{t('environmental.invitationThanks')}</p>
              <div className="mt-4 text-3xl">🌿 🇨🇷</div>
            </div>
          </div>

          <p className="text-center text-gray-400 text-sm mt-10">
            {t('environmental.lastUpdated')}
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EnvironmentalPolicyPage;
